import client from './client';

// Keep LEARNING/NOTE for backward compatibility with legacy rows already in DB.
// New UI flows currently create INBOX/APPLICATION/TASK only.
export type WorkItemType = 'APPLICATION' | 'TASK' | 'LEARNING' | 'NOTE' | 'INBOX';
export type WorkItemState = 'TRIAGE' | 'ACTIVE' | 'DONE' | 'ARCHIVED';
export type ClassificationSource =
    | 'heuristic'
    | 'ai_enrichment'
    | 'user_override'
    | 'bulk_user_approved'
    | 'migration'
    | 'system_default'
    | 'unknown';
export type ConfidenceBand = 'none' | 'zero' | 'low' | 'medium' | 'high';
export type OverrideActorType = 'user' | 'bulk_user_approved' | 'system' | 'unknown';
export type Attention = 'action' | 'watch' | 'ignore';

/**
 * Shape produced by backend `merge_source_lineage` / WorkItemSchema (JSON object).
 * Extra keys may appear from migrations or future API versions.
 */
export interface WorkItemSourceLineage {
    source_type?: string;
    source_id?: string;
    mailbox_id?: string | number | null;
    document_id?: string | null;
    created_from?: string;
    classification_influences?: unknown[];
    [key: string]: unknown;
}

export const STATE_TRIAGE = 'TRIAGE';
export const STATE_ACTIVE = 'ACTIVE';
export const STATE_DONE = 'DONE';
export const STATE_ARCHIVED = 'ARCHIVED';

/** C29-1: computed scam safety for an email item. No body text, no score. */
export interface WorkItemSafety {
    level: 'medium' | 'high';
    reasons: string[];
    /** Quarantined until the person decides. */
    held: boolean;
    /** The person's verdict, only while it names the item's current safety message. */
    verdict: 'safe' | 'scam' | null;
    /** False for a prompt-injection hold: the verbs are not offered. */
    decidable: boolean;
    /** Identity and revision of the evidence behind this decision. */
    message_id?: string | null;
    revision?: string | null;
}

export type SafetyVerdict = 'safe' | 'scam';

/**
 * Held, or high with no verdict yet: Today keeps it off the board in any open state
 * (C29-1 AC10). "High is held off Today" holds even when a body fetch raised it.
 */
export function isOffToday(safety: WorkItemSafety | null | undefined): boolean {
    if (!safety) return false;
    return safety.held || (safety.level === 'high' && safety.verdict == null);
}

/** "Possible scam" mark: held, undecided high, or called a scam by the person. */
export function showsScamMark(safety: WorkItemSafety | null | undefined): boolean {
    return isOffToday(safety) || safety?.verdict === 'scam';
}

export interface WorkItem {
    id: string;
    type?: string;
    domain?: string;
    state?: string;
    title?: string | null;
    summary?: string | null;
    tags?: string[];
    meta?: Record<string, any>;

    // Legacy fields (still returned for backward compatibility)
    company: string;
    job_title: string;
    status: string;
    applied_at: string;
    source?: string;
    confidence_score?: number;
    classification_source?: ClassificationSource | null;
    confidence_numeric?: number | null;
    confidence_band?: ConfidenceBand | null;
    evidence_summary?: string | null;
    override_actor_type?: OverrideActorType | null;
    override_set_at?: string | null;
    source_lineage?: WorkItemSourceLineage;
    provenance_updated_at?: string | null;
    /** Story 7.8: derived from shared backend predicate when present on API payloads. */
    suspicious_classification?: boolean | null;
    needs_action?: boolean;
    attention?: Attention | null;
    attention_reason?: string | null;
    attention_source?: string | null;
    source_key?: string | null;
    thread_key?: string | null;
    merge_key?: string | null;
    merge_suggestion?: { target_id: string; title?: string | null; kind: string } | null;
    safety?: WorkItemSafety | null;
    snoozed_until?: string | null;
    due_date?: string | null;  // Action date - when you need to act
    event_date?: string | null;  // Optional - when the event actually happens
    event_type?: string | null;  // interview, meeting, flight, deadline, etc.
    prep_days_override?: number | null;  // Per-item override for prep time
    last_activity_at?: string | null;
    last_updated?: string;
    notes?: string | null;
    gmail_message_id?: string | null; // ID of the source email in Gmail
    document_id?: string | null;
}

export type SyncStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'IDLE';

export interface CurrentUserResponse {
    google_connected: boolean;
    email?: string;
    sync_status?: SyncStatus | null;
}

export type WorkItemUpdate = Partial<
    Pick<
        WorkItem,
        | 'type'
        | 'domain'
        | 'state'
        | 'needs_action'
        | 'snoozed_until'
        | 'due_date'
        | 'event_date'
        | 'event_type'
        | 'prep_days_override'
        | 'title'
        | 'summary'
        | 'tags'
        | 'meta'
        | 'company'
        | 'job_title'
        | 'status'
        | 'notes'
        | 'confidence_score'
        | 'document_id'
    >
> & {
    confirm_domain_placement?: boolean;
    restore_best_guess?: boolean;
    meta_clear?: string[];
};

// Sanity Bar counts for trust metrics
export interface SanityCounts {
    open: number;
    overdue: number;
    dueToday: number;
    dueThisWeek: number;
    triage: number;
    needsAction: number;
    byDomain: Record<string, number>;
    byType: Record<string, number>;
    bySource: Record<string, number>;
}

export interface ExtractedTextResponse {
    filename: string;
    file_type: 'docx' | 'xlsx' | 'csv';
    text: string;
    truncated: boolean;
    char_count: number;
    max_chars?: number | null;
}

export interface SourceFamilyTask {
    id: string;
    title: string;
    due_date?: string | null;
    state?: string | null;
    snoozed_until?: string | null;
}

export interface SourceFamily {
    discoverable: boolean;
    source_kind: 'email' | 'document' | null;
    source_title: string;
    source_home_id: string | null;
    tasks: SourceFamilyTask[];
}

export interface SuggestedTask {
    title: string;
    summary: string;
    domain: string;
    due_date?: string | null;
    priority: string;
}

export interface InvoiceData {
    vendor?: string | null;
    invoice_number?: string | null;
    total_amount?: string | null;
    due_date?: string | null;
    bank_info_found: boolean;
}

export interface TextAnalysisResponse {
    entity: string;
    title?: string | null;
    status?: string | null;
    domain?: string | null;
    decision_needed?: boolean;
    confidence?: number;
    summary?: string | null;
    next_step?: string | null;
    job_title?: string | null;
    due_date?: string | null;
    notes?: string | null;

    // Multi-task & Invoice support
    suggested_tasks?: SuggestedTask[];
    invoice_data?: InvoiceData | null;
    sensitive_data_flag?: boolean;

    tokens_used?: number;
    api_unavailable?: boolean;
}

function createRequestIdempotencyKey(prefix: string, id: string): string {
    const random =
        typeof globalThis !== 'undefined' &&
        globalThis.crypto &&
        typeof globalThis.crypto.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}:${id}:${random}`;
}

/** One key per work item while a request is in flight so double-clicks / overlapping calls dedupe on the server. */
const enrichWorkItemIdempotencyKeyInFlight = new Map<string, string>();

export const workItemsApi = {
    getWorkItems: async (params?: {
        page?: number;
        limit?: number;
        type?: string;
        domain?: string;
        state?: string;
        needs_action?: boolean;
        company?: string;
        search?: string;
        sort_by?: string;
        sort_order?: string;
    }, options: { signal?: AbortSignal } = {}): Promise<{ items: WorkItem[]; total: number; page: number; size: number }> => {
        const response = await client.get('/work-items', { params, signal: options.signal });
        return response.data;
    },

    getWorkItem: async (id: string): Promise<WorkItem> => {
        const response = await client.get(`/work-items/${id}`);
        return response.data;
    },

    getSourceFamily: async (id: string): Promise<SourceFamily> => {
        const response = await client.get(`/work-items/${id}/family`);
        return response.data;
    },

    createWorkItem: async (data: WorkItemUpdate): Promise<WorkItem> => {
        const response = await client.post('/work-items', data);
        return response.data;
    },

    updateWorkItem: async (id: string, updates: WorkItemUpdate): Promise<WorkItem> => {
        const response = await client.patch(`/work-items/${id}`, updates);
        return response.data;
    },

    putSourceRule: async (
        id: string,
        attention: Attention,
    ): Promise<{
        rule: { source_key: string; attention: Attention; updated_at: string | null };
        item: WorkItem;
        applied_count: number;
        applied_ids: string[];
        previous_attention?: Attention | null;
        prior_items?: Array<{
            id: string;
            state?: string | null;
            needs_action?: boolean | null;
            attention?: Attention | null;
            attention_reason?: string | null;
            attention_source?: string | null;
        }>;
    }> => {
        const response = await client.put(`/work-items/${id}/source-rule`, { attention });
        return response.data;
    },

    undoSourceRule: async (
        id: string,
        payload: {
            previous_attention?: Attention | null;
            prior_items?: Array<{
                id: string;
                state?: string | null;
                needs_action?: boolean | null;
                attention?: Attention | null;
                attention_reason?: string | null;
                attention_source?: string | null;
            }>;
        },
    ): Promise<{ items: WorkItem[] }> => {
        const response = await client.post(`/work-items/${id}/source-rule/undo`, payload);
        return response.data;
    },

    attachMerge: async (id: string, targetId: string): Promise<{
        item: WorkItem;
        source_id: string;
        merged: boolean;
    }> => {
        const response = await client.post(`/work-items/${id}/attach`, { target_id: targetId });
        return response.data;
    },

    /** C29-1: "Looks safe" or "It's a scam"; the server keeps what Undo restores. */
    putSafetyVerdict: async (id: string, verdict: SafetyVerdict, expectedRevision: string): Promise<WorkItem> => {
        const response = await client.put(`/work-items/${id}/safety-verdict`, { verdict, expected_revision: expectedRevision });
        return response.data;
    },

    undoSafetyVerdict: async (id: string): Promise<WorkItem> => {
        const response = await client.post(`/work-items/${id}/safety-verdict/undo`);
        return response.data;
    },

    unmergeLast: async (id: string): Promise<{
        item: WorkItem;
        restored_item: WorkItem;
        undone_message_id: string;
    }> => {
        const response = await client.post(`/work-items/${id}/unmerge-last`);
        return response.data;
    },

    enrichWorkItem: async (
        id: string,
        options?: { force_overwrite?: boolean }
    ): Promise<{
        status: string;
        work_item_id: string;
        work_item: WorkItem;
        enrichment_result: Record<string, unknown>;
        updated_fields: string[];
    }> => {
        let idempotencyKey = enrichWorkItemIdempotencyKeyInFlight.get(id);
        if (!idempotencyKey) {
            idempotencyKey = createRequestIdempotencyKey('work-item-enrich', id);
            enrichWorkItemIdempotencyKeyInFlight.set(id, idempotencyKey);
        }
        try {
            const response = await client.post(`/ai/enrich-work-item/${id}`, options ?? {}, {
                headers: {
                    'Idempotency-Key': idempotencyKey,
                },
            });
            return response.data;
        } finally {
            enrichWorkItemIdempotencyKeyInFlight.delete(id);
        }
    },

    bulkUpdateWorkItems: async (
        ids: string[],
        updates: WorkItemUpdate,
        match?: { attention?: Attention | null; state?: string | null },
    ): Promise<{ updated_count: number; failed_count: number; updated_ids: string[] }> => {
        const response = await client.post('/work-items/bulk', {
            item_ids: ids,
            update: updates,
            ...(match ? { match } : {}),
        });
        return response.data;
    },

    analyzeDocument: async (file: File, context: string): Promise<any> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('context', context);
        const response = await client.post('/documents/analyze', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    extractTextFromFile: async (file: File): Promise<ExtractedTextResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await client.post('/files/extract-text', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    analyzeText: async (payload: { text: string; filename?: string; context?: string }): Promise<TextAnalysisResponse> => {
        const response = await client.post('/ai/analyze-text', payload);
        return response.data;
    },

    deleteWorkItem: async (id: string): Promise<void> => {
        await client.delete(`/work-items/${id}`);
    },

    getCurrentUser: async (options: { signal?: AbortSignal } = {}): Promise<CurrentUserResponse> => {
        const response = await client.get('/user/me', { signal: options.signal });
        return response.data;
    },

    connectEmail: async (data: { start_date?: string | null; end_date?: string | null; query?: string | null }): Promise<any> => {
        const response = await client.post('/connect-email', data);
        return response.data;
    },

    getSanityCounts: async (): Promise<SanityCounts> => {
        const response = await client.get('/sanity-counts');
        return response.data;
    },
};

export default workItemsApi;
